import { useState, useEffect, useMemo, useLayoutEffect } from 'react'
import { ChevronRight, ChevronLeft, Navigation, LayoutGrid, List } from 'lucide-react'
import { PageContainer } from '@/components/layout/PageContainer'
import { useAuthStore } from '@/lib/store/authStore'
import { useMobileConfig } from './useMobileConfig'
import { usePreviewToken } from './usePreviewToken'
import { usePostMessage } from './usePostMessage'
import { PresetPills } from './PresetPills'
import { OverrideControls } from './OverrideControls'
import { PublishHeader } from './PublishHeader'
import { PublishBar } from './PublishBar'
import { MobileStudioLayout } from './MobileStudioLayout'
import { PhoneFrame } from './PhoneFrame'
import {
  PRESET_DEFAULTS,
  OVERRIDE_FIELDS,
  COLUMN_DEFAULTS,
  COLUMN_FIELDS,
  type PresetDefaults,
  type OverrideField,
  type ColumnField,
  type ColumnVisibility,
} from './preset-defaults'
import { ColumnChecklist } from './ColumnChecklist'
import { TabEditorSection } from './TabEditorSection'
import { DEFAULT_TAB_ITEMS, hydrateTabItems, type BottomTabItem } from './tab-defaults'
import type { MobilePreset } from '@/lib/api/controlPlaneClient'

const PRESETS: Array<{
  preset: MobilePreset
  nameTh: string
  nameEn: string
}> = [
  { preset: 'app-style', nameTh: 'แบบแอป', nameEn: 'App-style' },
  { preset: 'web-style', nameTh: 'แบบเว็บ', nameEn: 'Web-style' },
  { preset: 'minimal', nameTh: 'มินิมอล', nameEn: 'Minimal' },
]

function getOverridesFromConfig(
  config: { nav_style?: string; search_placement?: string; category_layout?: string; product_list_layout?: string; table_row_density?: string; table_image_size?: string; default_view?: string; preset?: string } | null,
  preset: MobilePreset
): PresetDefaults {
  const defaults = PRESET_DEFAULTS[preset]
  if (!config) return { ...defaults }
  return {
    nav_style: (config.nav_style as PresetDefaults['nav_style']) || defaults.nav_style,
    search_placement: (config.search_placement as PresetDefaults['search_placement']) || defaults.search_placement,
    category_layout: (config.category_layout as PresetDefaults['category_layout']) || defaults.category_layout,
    product_list_layout: (config.product_list_layout as PresetDefaults['product_list_layout']) || defaults.product_list_layout,
    table_row_density: (config.table_row_density as PresetDefaults['table_row_density']) || defaults.table_row_density,
    table_image_size: (config.table_image_size as PresetDefaults['table_image_size']) || defaults.table_image_size,
    default_view: (config.default_view as PresetDefaults['default_view']) || defaults.default_view,
  }
}

function getColumnVisibilityFromConfig(
  config: { table_show_sku?: number; table_show_collections?: number; table_show_price?: number; table_show_add_to_cart?: number } | null,
  preset: MobilePreset
): ColumnVisibility {
  const defaults = COLUMN_DEFAULTS[preset]
  if (!config) return { ...defaults }
  return {
    table_show_sku: config.table_show_sku != null ? config.table_show_sku !== 0 : defaults.table_show_sku,
    table_show_collections: config.table_show_collections != null ? config.table_show_collections !== 0 : defaults.table_show_collections,
    table_show_price: config.table_show_price != null ? config.table_show_price !== 0 : defaults.table_show_price,
    table_show_add_to_cart: config.table_show_add_to_cart != null ? config.table_show_add_to_cart !== 0 : defaults.table_show_add_to_cart,
  }
}

type SidebarPanel = 'main' | 'navigation' | 'content-layout' | 'table-view'

export function MobileDesignPage() {
  const authMode = useAuthStore((s) => s.authMode)
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl)
  const { config, isLoading, publish, isPublishing } = useMobileConfig()
  const { token: previewToken, isLoading: tokenLoading, error: tokenError, retry: tokenRetry } = usePreviewToken(!!storefrontUrl)
  const [selectedPreset, setSelectedPreset] = useState<MobilePreset | null>(null)
  const [overrides, setOverrides] = useState<PresetDefaults>(PRESET_DEFAULTS['web-style'])
  const [tabItems, setTabItems] = useState<BottomTabItem[]>(DEFAULT_TAB_ITEMS)
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(COLUMN_DEFAULTS['web-style'])
  const [sidebarPanel, setSidebarPanel] = useState<SidebarPanel>('main')

  // PostMessage hook — must be before early returns (Hook rules)
  const { sendConfigUpdate, sendTokenRefresh } = usePostMessage(storefrontUrl ?? null)

  // Lock body scroll — prevents scrollbar gutter from creating a right gap
  useLayoutEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Capture initial token (set once, never changes) so iframe src stays stable
  const [initialToken, setInitialToken] = useState<string | null>(null)

  useEffect(() => {
    if (previewToken && initialToken === null) {
      setInitialToken(previewToken)
    }
  }, [previewToken, initialToken])

  // Send token refresh via postMessage (skip initial token — that's in the iframe src)
  useEffect(() => {
    if (previewToken && initialToken && previewToken !== initialToken) {
      sendTokenRefresh(previewToken)
    }
  }, [previewToken, initialToken, sendTokenRefresh])

  const activePreset = config?.preset ?? 'web-style'
  const effectivePreset = selectedPreset ?? activePreset

  useEffect(() => {
    if (config) {
      setOverrides(getOverridesFromConfig(config, activePreset))
      setTabItems(hydrateTabItems(config.bottom_tab_items))
      setColumnVisibility(getColumnVisibilityFromConfig(config, activePreset))
    }
  }, [config, activePreset])

  // Send config update to iframe on every control change
  useEffect(() => {
    sendConfigUpdate({
      preset: effectivePreset,
      ...overrides,
      ...columnVisibility,
      bottom_tab_items: tabItems,
    })
  }, [effectivePreset, overrides, columnVisibility, tabItems, sendConfigUpdate])

  // Auto-navigate back to main when nav_style changes away from bottom-tab-bar
  // (the navigation panel content changes — tab editor disappears)
  // No auto-nav needed: tab editor is now inside the navigation panel itself

  const hasPresetChange = selectedPreset !== null && selectedPreset !== activePreset
  const hasOverrideChange = config
    ? OVERRIDE_FIELDS.some(
        (f) => overrides[f] !== (config[f as keyof typeof config] || PRESET_DEFAULTS[activePreset][f])
      )
    : false
  const hasTabChange = config?.bottom_tab_items
    ? JSON.stringify(tabItems) !== JSON.stringify(hydrateTabItems(config.bottom_tab_items))
    : JSON.stringify(tabItems) !== JSON.stringify(DEFAULT_TAB_ITEMS)
  const hasColumnChange = config
    ? COLUMN_FIELDS.some((f) => {
        const saved = config[f as keyof typeof config]
        const savedBool = saved != null ? saved !== 0 : COLUMN_DEFAULTS[activePreset][f]
        return columnVisibility[f] !== savedBool
      })
    : false
  const hasPendingChange = hasPresetChange || hasOverrideChange || hasTabChange || hasColumnChange

  // Build preview URL with INITIAL token only (stable — no iframe reloads on token refresh)
  // When Table View panel is active, navigate to /search?view=table so table rows are visible
  const previewSrc = useMemo(() => {
    if (!storefrontUrl || !initialToken) return null
    const url = new URL(storefrontUrl)
    if (sidebarPanel === 'table-view') {
      url.pathname = '/search'
      url.searchParams.set('view', 'table')
    }
    url.searchParams.set('mobile_preview', 'true')
    url.searchParams.set('preview_token', initialToken)
    return url.toString()
  }, [storefrontUrl, initialToken, sidebarPanel])

  function handlePresetSelect(preset: MobilePreset) {
    if (preset === activePreset && !selectedPreset) return
    if (preset === selectedPreset) return
    if (preset === activePreset) {
      setSelectedPreset(null)
      setOverrides(getOverridesFromConfig(config, activePreset))
      setColumnVisibility(getColumnVisibilityFromConfig(config, activePreset))
    } else {
      setSelectedPreset(preset)
      setOverrides({ ...PRESET_DEFAULTS[preset] })
      setColumnVisibility({ ...COLUMN_DEFAULTS[preset] })
    }
  }

  function handleOverrideChange(field: OverrideField, value: string) {
    setOverrides((prev) => ({ ...prev, [field]: value }))
  }

  function handleResetField(field: OverrideField) {
    const defaults = PRESET_DEFAULTS[effectivePreset]
    setOverrides((prev) => ({ ...prev, [field]: defaults[field] }))
  }

  function handlePublish() {
    publish(
      {
        preset: effectivePreset,
        ...overrides,
        table_show_sku: columnVisibility.table_show_sku ? 1 : 0,
        table_show_collections: columnVisibility.table_show_collections ? 1 : 0,
        table_show_price: columnVisibility.table_show_price ? 1 : 0,
        table_show_add_to_cart: columnVisibility.table_show_add_to_cart ? 1 : 0,
        bottom_tab_items: tabItems.map(({ id, ...rest }) => rest),
      },
      { onSuccess: () => setSelectedPreset(null) }
    )
  }

  function handleCancel() {
    setSelectedPreset(null)
    setOverrides(getOverridesFromConfig(config, activePreset))
    setColumnVisibility(getColumnVisibilityFromConfig(config, activePreset))
    setTabItems(hydrateTabItems(config?.bottom_tab_items))
  }

  // Non-control-plane mode — keep PageContainer
  if (authMode !== 'control_plane') {
    return (
      <PageContainer>
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-2">ดีไซน์มือถือ</h2>
          <p className="text-muted-foreground">
            ฟีเจอร์นี้ใช้ได้เฉพาะในโหมด Production เท่านั้น
          </p>
        </div>
      </PageContainer>
    )
  }

  const activePresetLabel = PRESETS.find((p) => p.preset === activePreset)?.nameEn ?? activePreset

  const showTabEditor = overrides.nav_style === 'bottom-tab-bar'

  const sidebarContent = (
    <div className="relative overflow-hidden">
      {/* Panel slider — translateX to show main or sub-panel */}
      <div
        className="flex transition-transform duration-250 ease-out"
        style={{ transform: sidebarPanel === 'main' ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        {/* Panel 1: Main */}
        <div className="w-full shrink-0 p-5">
          <PublishHeader
            activePresetLabel={activePresetLabel}
            hasPendingChange={hasPendingChange}
            isPublishing={isPublishing}
            isLoading={isLoading}
            onPublish={handlePublish}
            onCancel={handleCancel}
          />

          <PresetPills
            presets={PRESETS}
            activePreset={activePreset}
            selectedPreset={selectedPreset}
            effectivePreset={effectivePreset}
            onSelect={handlePresetSelect}
            isLoading={isLoading}
          />

          {/* Drill-down menu items */}
          {!isLoading && (
            <div className="mt-5 border-t border-gray-200 pt-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">ส่วนประกอบ</div>
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => setSidebarPanel('navigation')}
                  className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <Navigation className="size-4 text-gray-400" />
                  <span className="flex-1 text-[13px] font-medium text-gray-700">การนำทาง</span>
                  <ChevronRight className="size-4 text-gray-300" />
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarPanel('content-layout')}
                  className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <LayoutGrid className="size-4 text-gray-400" />
                  <span className="flex-1 text-[13px] font-medium text-gray-700">เลย์เอาต์หน้าร้าน</span>
                  <ChevronRight className="size-4 text-gray-300" />
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarPanel('table-view')}
                  className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <List className="size-4 text-gray-400" />
                  <span className="flex-1 text-[13px] font-medium text-gray-700">มุมมองตาราง</span>
                  <ChevronRight className="size-4 text-gray-300" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Panel 2: Dynamic sub-panel */}
        <div className="w-full shrink-0 p-5">
          <button
            type="button"
            onClick={() => setSidebarPanel('main')}
            className="mb-4 flex items-center gap-1 text-[13px] font-medium text-blue-600 transition-colors hover:text-blue-700"
          >
            <ChevronLeft className="size-4" />
            <span>กลับ</span>
          </button>

          {sidebarPanel === 'navigation' && (
            <>
              <OverrideControls
                preset={effectivePreset}
                overrides={overrides}
                onOverrideChange={handleOverrideChange}
                onResetField={handleResetField}
                isLoading={isLoading}
                title="การนำทาง"
                fields={['nav_style'] as const}
              />
              {showTabEditor && (
                <div className="mt-5 border-t border-gray-200 pt-4">
                  <TabEditorSection
                    tabItems={tabItems}
                    onTabItemsChange={setTabItems}
                    navStyle={overrides.nav_style}
                    isLoading={isLoading}
                  />
                </div>
              )}
            </>
          )}

          {sidebarPanel === 'content-layout' && (
            <OverrideControls
              preset={effectivePreset}
              overrides={overrides}
              onOverrideChange={handleOverrideChange}
              onResetField={handleResetField}
              isLoading={isLoading}
              title="เลย์เอาต์หน้าร้าน"
              fields={['search_placement', 'category_layout', 'product_list_layout'] as const}
            />
          )}

          {sidebarPanel === 'table-view' && (
            <>
              <OverrideControls
                preset={effectivePreset}
                overrides={overrides}
                onOverrideChange={handleOverrideChange}
                onResetField={handleResetField}
                isLoading={isLoading}
                title="มุมมองตาราง"
                fields={['default_view', 'table_row_density', 'table_image_size'] as const}
              />
              <ColumnChecklist
                values={columnVisibility}
                defaults={COLUMN_DEFAULTS[effectivePreset]}
                onChange={(field: ColumnField, checked: boolean) =>
                  setColumnVisibility((prev) => ({ ...prev, [field]: checked }))
                }
                onReset={() => setColumnVisibility({ ...COLUMN_DEFAULTS[effectivePreset] })}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className={`-mb-6 overflow-hidden ${hasPendingChange ? 'pb-16 md:pb-0' : ''}`}>
      <MobileStudioLayout
        sidebar={sidebarContent}
        preview={
          <PhoneFrame
            src={previewSrc}
            tokenLoading={tokenLoading}
            tokenError={tokenError}
            onTokenRetry={tokenRetry}
          />
        }
      />
      {/* Mobile publish bar — only on small screens */}
      {hasPendingChange && (
        <div className="md:hidden">
          <PublishBar
            onPublish={handlePublish}
            onCancel={handleCancel}
            isPublishing={isPublishing}
          />
        </div>
      )}
    </div>
  )
}
